import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api } from "../../lib/api";
import { Sparkles, IdCard, Home, Mail, QrCode, X, Gift } from "lucide-react";
import PortalCommunitySection from "../../components/PortalCommunitySection";
import { useSystemSettings, useFeatureEnabled } from "../../lib/systemSettings";
import { Button } from "../../components/ui/button";

/**
 * Kundenportal → Übersicht.
 *
 * Owner Review 2026-08-31 — Club Card ist Kernbestandteil des
 * Kundenkontos und wird IMMER angezeigt, sobald der Kunde eine
 * club_card_id besitzt. Nur die Punkte-Kachel und der Coming-Soon-
 * Hinweis für das Punkteprogramm sind noch feature-flagged.
 */
export default function PortalHome() {
  const [me, setMe] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const { brandShort, brandName } = useSystemSettings();
  const pointsOn = useFeatureEnabled("points");
  useEffect(() => { api.get("/customer/me").then((r) => setMe(r.data)); }, []);
  if (!me) return <div className="text-zinc-500">Lade…</div>;
  const hasCard = !!me.club_card_id;
  return (
    <div className="space-y-6" data-testid="portal-home">
      <div>
        <div className="text-3xl font-black uppercase tracking-tight">Hallo {me.first_name}</div>
        <div className="text-sm text-zinc-500">Deine {brandShort}-Übersicht.</div>
      </div>
      {(pointsOn || hasCard) && (
      <div className="grid gap-4 sm:grid-cols-2">
        {pointsOn && (
        <div className="border border-zinc-800 rounded-lg p-4 bg-zinc-900/70">
          <div className="flex items-center gap-2 text-cyan-400 text-xs uppercase tracking-wider">
            <Sparkles size={16} /> <span className="truncate">{brandShort} Punkte</span>
          </div>
          <div data-testid="portal-loop-points" className="text-3xl font-black mt-2">
            {Number(me.loop_points ?? 0).toLocaleString("de-DE")}
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            1&nbsp;€ Umsatz = 10 Punkte · nur nach abgeschlossenem Kauf
          </div>
        </div>
        )}
        {hasCard && (
        <div className="border border-zinc-800 rounded-lg p-4 bg-zinc-900/70 flex flex-col">
          <div className="flex items-center gap-2 text-cyan-400 text-xs uppercase tracking-wider">
            <IdCard size={16} /> <span className="truncate">{brandShort} Club Kartennummer</span>
          </div>
          <div data-testid="portal-card-id" className="text-xl font-mono mt-2 tracking-wider break-all">{me.club_card_id}</div>
          <Button
            data-testid="portal-club-qr-btn"
            onClick={() => setQrOpen(true)}
            className="mt-3 self-start bg-cyan-400 hover:bg-cyan-300 h-9"
          >
            <QrCode size={14} className="mr-2 shrink-0" /> QR Code
          </Button>
        </div>
        )}
      </div>
      )}
      <div className="border border-zinc-800 rounded-lg p-4 bg-zinc-900/70 space-y-3">
        <div className="text-xs uppercase tracking-wider text-cyan-400">Persönliche Daten</div>
        <div className="text-sm text-zinc-300 flex items-center gap-2"><Mail size={14} className="text-zinc-500" /> {me.email}</div>
        <div className="text-sm text-zinc-300 flex items-center gap-2"><Home size={14} className="text-zinc-500" />
          {[me.address_street, me.address_zip, me.address_city, me.address_country].filter(Boolean).join(", ") || "Keine Adresse hinterlegt"}
        </div>
        <div className="text-xs text-zinc-500">Kontostatus: {me.active ? (me.email_verified ? "aktiv" : "E-Mail nicht bestätigt") : "deaktiviert"}</div>
      </div>
      <PortalCommunitySection />

      {qrOpen && (
        <div
          data-testid="portal-club-qr-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          onClick={() => setQrOpen(false)}
        >
          <div
            className="w-full max-w-md bg-white text-zinc-950 rounded-lg shadow-2xl border-2 border-cyan-400 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              data-testid="portal-club-qr-close"
              onClick={() => setQrOpen(false)}
              aria-label="Schließen"
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center text-zinc-700"
            >
              <X size={18} />
            </button>
            <div className="p-6 sm:p-8 flex flex-col items-center gap-4">
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                {brandName ? `${brandName} Club Card` : "Club Card"}
              </div>
              <div className="bg-white p-2">
                <QRCodeSVG value={me.club_card_id || ""} size={260} level="H" includeMargin={false} />
              </div>
              <div
                data-testid="portal-club-qr-cardid"
                className="text-base sm:text-lg font-mono tracking-wider font-bold break-all text-center"
              >
                {me.club_card_id}
              </div>
              <div className="text-sm text-zinc-600 text-center">{me.first_name} {me.last_name}</div>
              <div className="text-[11px] text-zinc-500 text-center leading-relaxed">
                An der Kasse vorzeigen, damit dein Einkauf deinem Kundenkonto
                zugeordnet werden kann.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
